import React, { useRef, useState } from 'react';
import { ArrowUpRight, Car, Gauge, MapPin, Wrench } from 'lucide-react';
import { BRANDS, type Brand } from '../data/brands';
import { BrandLogo } from './BrandLogo';
export function BrandSection() {
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const timer = useRef<number | null>(null);
  const focusBrand = (id: string) => {
    const el = cardRefs.current[id];
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    setHighlighted(id);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHighlighted(null), 1800);
  };
  return <>
      {/* Moder trak — Za vas zastopamo */}
      <section aria-labelledby="brands-strip-title" className="w-full bg-aw-navy" data-id="element-23" data-mp="3tqrcnm6ia3d2">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-9 lg:px-10" data-id="element-24" data-mp="3tsudz2d3zix3">
          <h2 id="brands-strip-title" className="text-center text-lg text-white/90" data-id="element-25" data-mp="3oygeoovbsrn5">
            Za vas zastopamo
          </h2>
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-7 sm:gap-x-14" data-id="element-26" data-mp="25dmvfpmdt3au">
            {BRANDS.map(brand => <li key={brand.id} data-id="element-27" data-mp="1vm8y9ohwkfkv">
                <button type="button" onClick={() => focusBrand(brand.id)} aria-label={`Prikaži ponudbo znamke ${brand.name}`} className="block opacity-85 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-aw-accent focus-visible:ring-offset-4 focus-visible:ring-offset-aw-navy" data-id="element-28" data-mp="1vl7emvmtmkrj">
                  <BrandLogo brand={brand} color="white" size={30} data-id="element-29" data-mp="1hloeoq45yfqb" />
                </button>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Kartice znamk */}
      <section aria-labelledby="brands-grid-title" className="w-full bg-slate-50" data-id="element-30" data-mp="3bzpdjazpr2qe">
        <div className="mx-auto w-full max-w-[1360px] px-5 py-16 lg:px-10" data-id="element-31" data-mp="3bxd09b4whgjo">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-32" data-mp="39mlu08ki0rlr">
            <div data-id="element-33" data-mp="35ie4i967v7mh">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-aw-accent" data-id="element-34" data-mp="35hw2evwvhz6n">
                Naša ponudba
              </p>
              <h2 id="brands-grid-title" className="mt-2 font-heading text-2xl font-bold text-aw-navy sm:text-3xl" data-id="element-35" data-mp="2vl9ic9cdfc5o">
                Izberite blagovno znamko
              </h2>
              <p className="mt-2 max-w-xl text-sm text-slate-500" data-id="element-36" data-mp="3o9f5f2qwdd8o">
                Zaloga vozil, testna vožnja in servis — vse na enem mestu, za vsako znamko posebej.
              </p>
            </div>
            <a href="#" className="group flex items-center gap-2 text-sm font-semibold text-aw-navy hover:text-aw-accent" data-id="element-37" data-mp="3ekx6kr26hg7o">
              Vsa vozila na zalogi
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-38" data-mp="b357kspmjvzz" />
            </a>
          </div>

          <ul className="mt-10 grid auto-rows-fr gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-39" data-mp="ig4fvn5soss3">
            {BRANDS.map(brand => <li key={brand.id} id={`znamka-${brand.id}`} ref={el => {
            cardRefs.current[brand.id] = el;
          }} className="scroll-mt-28" data-id="element-40" data-mp="nfhet69xxqb1">
                <BrandCard brand={brand} highlighted={highlighted === brand.id} data-id="element-41" data-mp="11ce3b12l6vor" />
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}
function BrandCard({
  brand,
  highlighted
}: {
  brand: Brand;
  highlighted: boolean;
}) {
  return <article className={`flex h-full flex-col border bg-white p-6 transition-all duration-300 hover:shadow-lg ${highlighted ? 'border-aw-accent shadow-lg ring-2 ring-aw-accent' : 'border-slate-200'}`} data-id="element-42" data-mp="1g2bttnnznxh4">
      <div className="flex h-12 items-start" data-id="element-43" data-mp="1p28bh5bynw2o">
        <BrandLogo brand={brand} color="navy" size={40} data-id="element-44" data-mp="1p0xdjm575ijh" />
      </div>

      <h3 className="mt-5 font-heading text-xl font-bold text-aw-navy" data-id="element-45" data-mp="2mqld5ba30eaj">{brand.name}</h3>
      <p className="mt-1 text-sm text-slate-500" data-id="element-46" data-mp="2hrqohspfnvii">{brand.tagline}</p>

      <ul className="mt-4 flex flex-wrap gap-1.5" data-id="element-47" data-mp="2vjyff3u1cs9a">
        {brand.locations.map(loc => <li key={loc} className="flex items-center gap-1 bg-slate-50 px-2 py-1 text-[11px] text-slate-500" data-id="element-48" data-mp="2riv0cjjtb094">
            <MapPin className="h-3 w-3" aria-hidden="true" data-id="element-49" data-mp="23ttqcrvbzh1q" />
            {loc}
          </li>)}
      </ul>

      <ul className="mt-auto border-t border-slate-100 pt-3" data-id="element-50" data-mp="1yvs5c1ech7nm">
        <CardAction label={`Zaloga vozil ${brand.name}`} Icon={Car} data-id="element-51" data-mp="2ctytgpthrta3" />
        <CardAction label="Prijava na testno vožnjo" Icon={Gauge} data-id="element-52" data-mp="27um0dnc11pz2" />
        {brand.hasService && <CardAction label="Servis in cenitev" Icon={Wrench} data-id="element-53" data-mp="27wyluagzuigv" />}
      </ul>
    </article>;
}
function CardAction({
  label,
  Icon
}: {
  label: string;
  Icon: typeof Car;
}) {
  return <li data-id="element-54" data-mp="3j9iwtiajjhgg">
      <a href="#" className="group flex items-center gap-2.5 py-2.5 text-sm text-slate-600 transition-colors hover:text-aw-navy" data-id="element-55" data-mp="3jc573potgb2d">
        <Icon className="h-4 w-4 shrink-0 text-aw-navy" aria-hidden="true" data-id="element-56" data-mp="3vtfx7x79zwzq" />
        <span className="font-medium" data-id="element-57" data-mp="3ht56qv9vokf3">{label}</span>
        <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-aw-accent" data-id="element-58" data-mp="3hug9qsvi1o55" />
      </a>
    </li>;
}